import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Outlet, useLocation } from "react-router-dom";
import { colors, css, fonts, frames, icons, useMediaQuery } from "../../Styles";
import { Overlay, StatusBar, LogoDisplay, StatusList, MenuButton, LanguageSelector } from "../../Components";
import { ButtonTypes, Clock, ListStyle_Types } from '../../Definitions';
import { GlobalMenu, SystemMenu } from "../Menus";
import { GlobalMenu_Pages, SystemMenu_Pages } from '../pages';
import { useUser } from '../../Services';
import { NewButtonList } from '../../Components/BaseComponents/newButton';

// #region | fullViewPanelLayoutDesktop
const FullViewPanelLayoutDesktop = () => {
  const { serverIp, getServerIp } = useUser()
  
  useEffect(() => {
    getServerIp()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  return (
    <frames.FullViewPanel>
      <LogoDisplay overrides={{ gridArea: "logoDisplay" }} ChooseSite selectedPage={GlobalMenu_Pages.kpi}/>
      <GlobalMenu overrides={{ gridArea: "globalMenu" }}/>
      <SystemMenu overrides={{ gridArea: "systemMenu" }} />
      <StatusBar overrides={{ gridArea: "statusBar" }} />
      <frames.LanguageSelector>
        <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{serverIp}</p>
        <div style={{...css.LanguageSelector_seperator}}/>
        <Clock />
        <div style={{...css.LanguageSelector_seperator}}/>
        <LanguageSelector isPrimary/>
      </frames.LanguageSelector> 
      <div style={{ gridArea: "page_outer", overflow: "hidden"}}>
        <Outlet /> 
      </div>
    </frames.FullViewPanel>
  );
};
// #endregion
// ---------------------------------

// #region | FullViewPanelLayoutMobile
const FullViewPanelLayoutMobile = () => {
  const {t} = useTranslation()
  const location = useLocation();
  const [showMenu, setShowMenu] = useState<"GLOBAL" | "SYSTEM" | "STATUS" | null>(null)
  const [menuOpen, setMenuOpen] = useState(false);

  // MediaQuries
  const groupMenu2columns = useMediaQuery(`(max-width: 454px)`)
  const groupMenu3columns = useMediaQuery(`(min-width: 455px) and (max-width: 590px)`)

  // Btn Router
  const BtnFunc_Router = (btn_index: number) => {
    switch (btn_index) {
      case 0:
        setShowMenu("GLOBAL")
        break;
      case 1:
        setShowMenu("SYSTEM")
        break;
      case 2:
        setShowMenu("STATUS")
        break;
      default:
        break;
    }
  };

  // Active menu item
  useEffect(() => {
    const path = location.pathname.toLowerCase();
  
    const globalMenuPages = Object.values(GlobalMenu_Pages).map(page => `/${page}`);
    const systemMenuPages = Object.values(SystemMenu_Pages).map(page => `/${page}`);
  
    if (globalMenuPages.some(p => path.startsWith(p))) {
      BtnFunc_Router(0);
    } else if (systemMenuPages.some(p => path.startsWith(p))) {
      BtnFunc_Router(1);
    } else {
      BtnFunc_Router(0);
    }
  }, [location]);
  
  // Automatic close menu
  const toggleComponents = () => {
    setMenuOpen(!menuOpen);
  };
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return ( <>
    <frames.FullViewPanel>
      {createPortal (<MenuButton menuState={menuOpen} onClick={toggleComponents}/>, document.body)}

      {menuOpen ? createPortal(<>
        <Overlay
          overrides={{ zIndex: menuOpen ? 99999 : 0, blur: true }}
          content={
            <frames.Menu_Outer>
              <div style={{ gridArea: "1/1/2/2" }}><LogoDisplay ChooseSite selectedPage={GlobalMenu_Pages.kpi} overrides={{ borderRadius: (showMenu === "SYSTEM" || showMenu === "STATUS") ? "10px 10px 0px 0px" : "10px" }} /></div>
              
              <div style={{ gridArea: "2/1/3/2", overflow: "hidden", display: "flex", alignItems: "flex-end" }}>
                {showMenu === "GLOBAL" && <GlobalMenu />}
                {showMenu === "SYSTEM" && <SystemMenu />}
                {showMenu === "STATUS" && <StatusList />}
              </div>
                
              <div style={{ gridArea: "3/1/4/2" }}>
                <div style={{ width: "100%", height: "fit-content", maxWidth: "555px", padding: "7.5px", boxSizing: "border-box", backgroundColor: colors.greyDarkPrimary, borderRadius: (showMenu !== null) ? "0px 0px 10px 10px" : "10px", margin: "0px auto", display: "flex", justifyContent: "center" }}>
                  <div style={{ width: groupMenu2columns ? "265px" : groupMenu3columns ? "400px" : "535px" }}>
                    <NewButtonList
                      contents={[t("menuHeaders.globalMenu", { defaultValue: "global menu" }), t("menuHeaders.systemMenu", { defaultValue: "system menu" }), t("menuHeaders.attentions", { defaultValue: "attentions" }), t("menuHeaders.language", { defaultValue: "language" })]}
                      onClick={(e) => BtnFunc_Router(e)}
                      buttonType={ButtonTypes.globalMenuButton}
                      overrides={(index) => ({
                        startEnhancerStyles: { width: "25px", height: "25px" },
                        baseStyles: { backgroundColor: (showMenu === "GLOBAL" && index === 0) || (showMenu === "SYSTEM" && index === 1) || (showMenu === "STATUS" && index === 2) ? colors.greyDarkSecondary : colors.greyDarkPrimary },
                        hoverStyles: { backgroundColor: colors.greyDarkSecondary }
                      })}
                      listOverrides={{
                        listStyle: ListStyle_Types.group
                      }}
                      startEnhancers={[
                        icons.globalSettings, icons.globalSettings, icons.globalSettings, icons.globalSettings
                      ]} />
                  </div>
                </div>
              </div>
            </frames.Menu_Outer>
          }
        />
      </>, document.body) : undefined }
      <div style={{ gridArea: "page_outer", overflow: "hidden"}}>
        <Outlet /> 
      </div>
    </frames.FullViewPanel>
  </> );
};
// #endregion
// ---------------------------------

const FullViewPanelLayout = () => {
  const mobile = useMediaQuery(`(max-width: 1000px)`);
  return ( <>
    {mobile ? <FullViewPanelLayoutMobile/> : <FullViewPanelLayoutDesktop/>}
  </> )
}

export default FullViewPanelLayout